import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { TixCompanyService } from '@tix/company/state';
import { Maybe } from '@tix/data-access';
import {
  TixUserPreferencesService,
  USER_PREFERENCE_KEYS
} from '@tix/shared/state';
import { LoggerService } from 'apps/purchasing-portal/src/app/logger.service';
import { Store } from '@ngrx/store';
import { UserSelector } from '@tix/auth/state';
import { TixCompanyProfile, User } from '@tix/shared/models';
import { take } from 'rxjs/operators';
import { TixConfirmDeleteDialogService } from 'libs/shared/ui/components/src/lib/dialog/confirm-delete-dialog/confirm-delete-dialog.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tix-company-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TixCompanyProfileComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() companyId: Maybe<string>;
  @Input() companyName: Maybe<string>;
  @Input() companyWebsite?: Maybe<string>;
  @Input() isloading?: boolean | undefined | null;
  @Output() saved: EventEmitter<TixCompanyProfile> =
    new EventEmitter<TixCompanyProfile>();

  profileForm: FormGroup;

  get name(): FormControl {
    return this.profileForm.get('companyName') as FormControl;
  }

  get website(): FormControl {
    return this.profileForm.get('companyWebsite') as FormControl;
  }

  get isDefaultCompany(): FormControl {
    return this.profileForm.get('isDefaultCompany') as FormControl;
  }

  get pristineOrInvalid(): boolean {
    return this.profileForm.pristine || this.profileForm.invalid;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private confirmDeleteDialogService: TixConfirmDeleteDialogService,
    private tixCompanyService: TixCompanyService,
    private userPreferencesService: TixUserPreferencesService,
    private router: Router,
    private logger: LoggerService,
    private store: Store,
    private analytics: TixAnalyticsService
  ) {}

  ngOnInit(): void {
    this.profileForm = new FormGroup({
      companyName: new FormControl(this.companyName, [Validators.required]),
      companyWebsite: new FormControl(this.companyWebsite),
      isDefaultCompany: new FormControl(false) // Start with false, will be updated by subscription
    });

    // Subscribe to preferences changes to update checkbox state
    this.userPreferencesService.preferences$
      .pipe(takeUntil(this.destroy$))
      .subscribe(preferences => {
        const defaultCompanyId =
          preferences[USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID];
        const isCurrentlyDefault = defaultCompanyId === this.companyId;
        this.isDefaultCompany.setValue(isCurrentlyDefault, {
          emitEvent: false
        });
      });

    // Listen for changes to the default company checkbox
    this.isDefaultCompany.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDefault: boolean) => {
        if (isDefault && this.companyId) {
          // Set this company as default
          this.userPreferencesService.setPreference(
            USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID,
            this.companyId
          );
        } else if (!isDefault) {
          // Remove this company as default if checkbox is unchecked
          this.userPreferencesService.removePreference(
            USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID
          );
        }
      });

    this.store
      .select(UserSelector.getAuthenticatedUser)
      .pipe(take(1))
      .subscribe((user: User | null) => {});

    // Log profile view
    this.store.select(UserSelector.getAuthenticatedUser).subscribe(user => {
      this.logger.info({
        message: 'Company profile viewed',
        event: 'company_profile_view',
        data: {
          companyId: this.companyId,
          companyName: this.companyName
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);

    if (
      changes['isloading'] &&
      changes['isloading'].previousValue === true &&
      changes['isloading'].currentValue === false
    ) {
      this.profileForm.markAsPristine();
    }

    this.profileForm?.patchValue({
      companyName:
        (changes['companyName']?.currentValue as string) ?? this.name.value,
      companyWebsite:
        (changes['companyWebsite']?.currentValue as string) ??
        this.website.value
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave(): void {
    if (this.profileForm.valid) {
      const profile: TixCompanyProfile = {
        companyId: this.companyId,
        name: this.name.value,
        url: this.website.value,
        state: 'Active',
        updatedAt: 'now()',
        updatedBy: '00fda0cf-5a5d-4821-9e05-d995720212ab'
      };

      this.store
        .select(UserSelector.getAuthenticatedUser)
        .pipe(take(1))
        .subscribe((user: User | null) => {
          this.logger.info({
            message: 'Company profile form submitted',
            event: 'company_profile_submit',
            data: {
              companyId: this.companyId,
              oldName: this.companyName,
              newName: profile.name,
              oldUrl: this.companyWebsite,
              newUrl: profile.url
            }
          });
          this.analytics.captureEvent('company_profile_submit', {
            companyId: this.companyId,
            oldName: this.companyName,
            newName: profile.name,
            oldUrl: this.companyWebsite,
            newUrl: profile.url
          });
        });

      this.saved.emit(profile);
    }
  }

  async deleteEvent(): Promise<void> {
    this.analytics.captureEvent('delete_company_clicked');
    const confirmed = await this.confirmDeleteDialogService.confirmDelete({
      confirmationText: this.companyName || ''
    });

    if (confirmed) {
      this.analytics.captureEvent('delete_company_confirmed');
      this.store
        .select(UserSelector.getAuthenticatedUser)
        .pipe(take(1))
        .subscribe((user: User | null) => {
          this.logger.info({
            message: 'Company deletion confirmed',
            event: 'company_delete_confirm',
            data: {
              companyId: this.companyId,
              companyName: this.companyName
            }
          });
          this.analytics.captureEvent('company_delete_confirm', {
            companyId: this.companyId,
            companyName: this.companyName
          });
        });

      await this.tixCompanyService.deleteCompany(this.companyId || '');

      this.store
        .select(UserSelector.getAuthenticatedUser)
        .pipe(take(1))
        .subscribe((user: User | null) => {
          this.logger.info({
            message: 'Company deleted',
            event: 'company_deleted',
            data: {
              companyId: this.companyId,
              companyName: this.companyName
            }
          });
          this.analytics.captureEvent('company_deleted', {
            companyId: this.companyId,
            companyName: this.companyName
          });
        });

      this.router.navigate(['/companies']);
    } else {
      this.analytics.captureEvent('delete_company_cancelled');
    }
  }

  private getChangedFields(): string[] {
    const changedFields: string[] = [];
    if (this.name.value !== this.companyName) changedFields.push('name');
    if (this.website.value !== this.companyWebsite)
      changedFields.push('website');
    return changedFields;
  }
}
